import React, { useMemo } from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  Autocomplete,
} from '@mui/material';
import {
  DIFFICULTIES,
  DOMAIN_NAMES,
  getAllCompetences,
  getCompetenceName,
  getNotionLabel,
  cycleToSchoolLevel,
} from '../../config/shared_config';
import { resolveParametersCycle } from '../../utils/taxonomyGenerationParams';

const CYCLE_OPTIONS = [
  { value: 2, label: 'Cycle 2 (CP-CE2)' },
  { value: 3, label: 'Cycle 3 (CM1-6ème)' },
  { value: 4, label: 'Cycle 4 (5ème-3ème)' },
];

/**
 * Les notions du référentiel, avec de quoi les afficher : libellé, compétence et domaine.
 * La valeur est l'IDENTIFIANT de notion — celui de la colonne `exercises.notion_id`.
 */
function optionsDeNotion() {
  return getAllCompetences().map((c) => ({
    id: c.notion,
    label: getNotionLabel(c.notion) || c.notion,
    domain: c.domain,
    competence: c.competence,
    domainLabel: DOMAIN_NAMES[c.domain] || c.domain,
    competenceLabel: getCompetenceName(c.domain, c.competence) || c.competence,
  }));
}

/**
 * Formulaire lisible des paramètres pédagogiques (theme, leçon, compétence, difficulté…).
 * Met à jour l'objet `parameters` complet.
 */
export default function ExerciseParametersSummaryForm({
  parameters = {},
  onChange,
  readOnly = false,
  gameType,
  // 🔴 La notion est la colonne `notion_id` de l'exercice, pas `parameters.competence`
  // (dont les clés ont changé deux fois : {domain, competence, notion} → {notionId}).
  notionId = null,
  onNotionChange,
}) {
  const notions = useMemo(() => optionsDeNotion(), []);
  const notionChoisie = notions.find((n) => n.id === notionId) || null;

  const patch = (partial) => {
    onChange({ ...parameters, ...partial });
  };

  const cycleValue = resolveParametersCycle(parameters);

  const handleCycleChange = (rawCycle) => {
    if (rawCycle === '') {
      patch({ cycle: undefined, schoolLevel: undefined, level: undefined });
      return;
    }
    const cycle = Number(rawCycle);
    const schoolLevel = cycleToSchoolLevel(cycle);
    patch({ cycle, schoolLevel, level: schoolLevel });
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
        Paramètres pédagogiques
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Thème"
            value={parameters.theme ?? ''}
            onChange={(e) => patch({ theme: e.target.value })}
            disabled={readOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Leçon"
            value={parameters.lesson ?? ''}
            onChange={(e) => patch({ lesson: e.target.value })}
            disabled={readOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Difficulté</InputLabel>
            <Select
              value={parameters.difficulty ?? ''}
              label="Difficulté"
              onChange={(e) => patch({ difficulty: e.target.value })}
              disabled={readOnly}
            >
              <MenuItem value="">
                <em>Non définie</em>
              </MenuItem>
              {Object.values(DIFFICULTIES).map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Cycle scolaire</InputLabel>
            <Select
              value={cycleValue === '' ? '' : cycleValue}
              label="Cycle scolaire"
              onChange={(e) => handleCycleChange(e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value="">
                <em>Non défini</em>
              </MenuItem>
              {CYCLE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Domaine"
            value={notionChoisie?.domainLabel ?? ''}
            placeholder={notionId ? notionId : 'sans notion'}
            InputLabelProps={{ shrink: true }}
            InputProps={{ readOnly: true }}
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            size="small"
            label="Compétence"
            value={notionChoisie?.competenceLabel ?? ''}
            InputLabelProps={{ shrink: true }}
            InputProps={{ readOnly: true }}
            disabled
          />
        </Grid>
        <Grid item xs={12} md={5}>
          {/* Le domaine et la compétence se DÉDUISENT de la notion : on ne choisit que
              celle-ci. Une notion inconnue du référentiel (fusionnée, supprimée)
              s'affiche par son identifiant pour qu'on la voie. */}
          <Autocomplete
            size="small"
            options={notions}
            value={notionChoisie}
            onChange={(e, option) => option && onNotionChange?.(option.id)}
            getOptionLabel={(option) => option.label || option.id}
            isOptionEqualToValue={(option, valeur) => option.id === valeur.id}
            groupBy={(option) => `${option.domainLabel} · ${option.competenceLabel}`}
            disableClearable
            disabled={readOnly || !onNotionChange}
            noOptionsText="Aucune notion"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Notion"
                placeholder={notionId && !notionChoisie ? `${notionId} (hors référentiel)` : 'sans notion'}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        {(gameType === 'word_lists' || gameType === 'dictation' || gameType === 'speed_game') && (
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Nombre de mots"
              type="number"
              value={parameters.totalWords ?? ''}
              onChange={(e) => patch({ totalWords: e.target.value ? Number(e.target.value) : undefined })}
              disabled={readOnly}
            />
          </Grid>
        )}
        {gameType === 'dictation' && (
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Mode dictée</InputLabel>
              <Select
                value={parameters.mode ?? 'text'}
                label="Mode dictée"
                onChange={(e) => patch({ mode: e.target.value })}
                disabled={readOnly}
              >
                <MenuItem value="text">Texte libre</MenuItem>
                <MenuItem value="word_lists">Depuis liste de mots</MenuItem>
                <MenuItem value="dictation">Dictée guidée</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
